/**
 * 어린이날 릴레이 방송 정보(스트리머 자리 + 합방 피날레).
 *
 * 시간은 모두 KST(한국 표준시) 기준 ISO 8601 문자열로 저장한다.
 * 클라이언트에서는 `new Date(...)`로 파싱한 뒤 현재 시각과 비교해 status를 계산한다.
 *
 * **주의** — 이 모듈의 `id` ('A' | 'B' | 'C')는 일정 표시용 슬롯 식별자일 뿐이며,
 * 서버 모델과는 무관하다. 서버는 코드 누적 카운트만 다루므로 클라이언트의
 * 코드 등록 흐름에서는 사용되지 않는다.
 */

/** 일정 표시용 슬롯 식별자 — 시각·색상 분기에만 사용된다. */
export type StreamerSlotId = 'A' | 'B' | 'C';

export interface Streamer {
  readonly id: StreamerSlotId;
  readonly name: string;
  readonly persona: string;
  readonly startAt: string;
  readonly endAt: string;
  readonly format: string;
  readonly channelUrl: string | null;
  readonly color: string;
  /**
   * 카드 좌측에 표시할 캐릭터 이미지 경로.
   *
   * `public/vts/*.png`에 위치한 정적 자산을 가리키며, 빌드 시 그대로 복사된다.
   * 작은 정사각 썸네일에서 인물의 얼굴이 잘 보이도록 VTS(VTuber)용 얼굴 위주
   * 이미지를 사용한다. 더 상세한 일러스트는 `profileImageUrl`을 참조한다.
   */
  readonly imageUrl: string;
  /**
   * 카드 클릭 시 열리는 상세 모달에 표시할 인물 프로필 이미지 경로.
   * `public/profile/*.png`에 위치하며, 캐릭터 이미지보다 더 상세한 일러스트.
   */
  readonly profileImageUrl: string;
  /**
   * 합방 피날레 카드의 좌측 3인 콜라주(작은 원형 아바타)에 사용할 이미지 경로.
   *
   * `public/ch/*.png`의 SD(슈퍼 디포르메) 캐릭터 일러스트를 가리킨다. 일반 카드의
   * `imageUrl`(VTS 전신 이미지)을 그대로 쓰면 60px 원형 안에서 인물 식별이 어렵기
   * 때문에, 콜라주에는 머리가 크고 식별이 쉬운 SD 컷을 별도로 사용한다.
   */
  readonly finaleAvatarUrl: string;
}

/**
 * 어린이날(2026-05-05) 릴레이 방송 가안.
 * PRD §7 타임라인을 반영했으며, 실제 일정 확정 시 이 배열만 수정하면 모든 화면에 반영된다.
 */
export const STREAMERS: readonly Streamer[] = [
  {
    id: 'A',
    name: '모로이 모나 선생님',
    persona: '다락유치원 튤립반 선배 모나쌤과 함께하는 어린이날 특별 이동수업! 7세 김주붕이가 되어 상냥하고 탱탱한 모나쌤과 독특한 일상으로 호감도를 쌓아보자',
    startAt: '2026-05-05T14:00:00+09:00',
    endAt: '2026-05-05T15:30:00+09:00',
    format: '',
    channelUrl: 'https://chzzk.naver.com/f795b445d35db5ece3d1d614c1355def',
    color: 'var(--color-streamer-a)',
    imageUrl: '/vts/mona.png',
    profileImageUrl: '/profile/mona.png',
    finaleAvatarUrl: '/ch/mona.png',
  },
  {
    id: 'B',
    name: '마롱 선생님',
    persona: '수줍음 많고 엉뚱한 천연계 미소녀 마롱쌤과 함께하는 두 번째 수업! 5세 둘구가 되어 주머니 속 음식으로 환심사고 퀴즈도 풀며 마롱쌤을 공략해보자!',
    startAt: '2026-05-05T15:30:00+09:00',
    endAt: '2026-05-05T16:30:00+09:00',
    format: '',
    channelUrl: 'https://chzzk.naver.com/21030ee8202e8e99785651805fe97212',
    color: 'var(--color-streamer-b)',
    imageUrl: '/vts/marong.png',
    profileImageUrl: '/profile/marong.png',
    finaleAvatarUrl: '/ch/marong.png',
  },
  {
    id: 'C',
    name: '깅나리 선생님',
    persona: '까다로운 면접부터 통과해야 하는 엘리트 다락유치원 나리쌤 클래스! 네코미미 미녀 나리쌤과 음쥐가 되어 면접 돌파부터 비밀스러운 짝사랑 편지까지 도전해보자!',
    startAt: '2026-05-05T16:30:00+09:00',
    endAt: '2026-05-05T17:30:00+09:00',
    format: '',
    channelUrl: 'https://chzzk.naver.com/c9392bfbbb1e52a3d022ed9b71c4a60f',
    color: 'var(--color-streamer-c)',
    imageUrl: '/vts/gingnari.png',
    profileImageUrl: '/profile/gingnari.png',
    finaleAvatarUrl: '/ch/nari.png',
  },
];

/**
 * 3인 솔로 방송이 끝난 뒤 22:00에 진행되는 합방 피날레.
 * 시청자가 채팅으로 진행 상황을 공유하며 함께 일러스트를 완성하는 공동 체험 시간.
 *
 * `Streamer`와 같은 구조를 따르되 `id`는 'finale'로 식별한다.
 */
export const FINALE = {
  id: 'finale' as const,
  name: '3인 합방',
  persona: '모나·마롱·나리 세 선생님과의 결혼 후 펼쳐지는 다락유치원 합방! 직접 유치원생이 된 좌충우돌 일상을 만나보세요.',
  startAt: '2026-05-05T17:30:00+09:00',
  endAt: '2026-05-05T19:00:00+09:00',
  format: '',
  /**
   * 피날레는 별도 모달 없이 합방 호스트(깅나리) 채널로 바로 이동한다.
   * 합방 방송이 깅나리 측에서 진행되기 때문.
   */
  channelUrl: 'https://chzzk.naver.com/c9392bfbbb1e52a3d022ed9b71c4a60f',
  /** 피날레는 한 색이 아닌 그라디언트 — CSS 측에서 처리 */
  color: 'gradient',
};

/**
 * 모든 일정 항목의 합집합 타입. UI 컴포넌트에선 둘 다 같은 카드 모양으로 그릴 수 있다.
 */
export type ScheduleItem = Streamer | typeof FINALE;
